import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError, api } from '../lib/api.js'

/**
 * `/account/cancel-deletion?token=…` (PRIV-001).
 *
 * Reachable signed out, and it has to be: requesting deletion revokes every
 * session, so by the time somebody changes their mind there is nothing to be
 * signed in as. The token from the email is the whole authority.
 *
 * It does not cancel on load. A link in an email is followed by prefetchers,
 * scanners and previews as well as by people, and a GET that undoes a decision
 * is a decision made by somebody's mail client.
 *
 * It does check on load (Q10), with a route that spends nothing: the page used
 * to announce "Your account is scheduled for deletion" for any token at all
 * and find out otherwise only when the button spent it. A refusal is the
 * cancel route's own sentence, in the place the missing-token sentence takes.
 */
const NO_ANSWER = 'Sonologue didn’t answer. Your account is unchanged — try again in a moment.'

export default function CancelDeletion() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const [state, setState] = useState({ busy: false, done: false, error: '' })
  const [check, setCheck] = useState({ status: 'checking', message: '' })

  useEffect(() => {
    document.title = 'Keep your account — Sonologue'
  }, [])

  useEffect(() => {
    if (!token) return undefined
    const controller = new AbortController()
    setCheck({ status: 'checking', message: '' })
    api
      .preflightDeletionCancel(token, { signal: controller.signal })
      .then(() => setCheck({ status: 'valid', message: '' }))
      .catch((error) => {
        if (error.name === 'AbortError') return
        setCheck({
          status: 'refused',
          message: error instanceof ApiError ? error.message : NO_ANSWER,
        })
      })
    return () => controller.abort()
  }, [token])

  async function keep() {
    setState({ busy: true, done: false, error: '' })
    try {
      await api.cancelDeletion(token)
      setState({ busy: false, done: true, error: '' })
    } catch (error) {
      setState({
        busy: false,
        done: false,
        // Not "that link did not work": the link was fine, the request never
        // arrived, and blaming it sends somebody hunting for a new email.
        error: error instanceof ApiError ? error.message : NO_ANSWER,
      })
    }
  }

  if (state.done) {
    return (
      <div className="card card-pad">
        <div className="empty-state">
          <h1 className="title-2">Your account is back</h1>
          <p>Nothing was deleted. Everything is where you left it.</p>
          <Link className="btn btn-primary" to="/login">
            Sign in
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="card card-pad">
      <div className="empty-state">
        <h1 className="title-2">Keep your account?</h1>
        {token && check.status === 'checking' ? (
          <p role="status">Checking your link…</p>
        ) : token && check.status === 'refused' ? (
          <p>{check.message}</p>
        ) : token ? (
          <p>
            Your account is scheduled for deletion. This puts it back exactly as it was — your
            ratings, reviews, lists and the people you follow are all still here until the date in
            your email.
          </p>
        ) : (
          // No session to sign in with, so the deadline on the link they
          // already have is the only way forward there is to name.
          <p>
            That link is missing its token. Use the whole link from the email. The link in your
            email works until the deletion date.
          </p>
        )}
        {state.error ? (
          <p className="form-alert error" role="alert">
            {state.error}
          </p>
        ) : null}
        {token && check.status === 'valid' ? (
          <button type="button" className="btn btn-primary" onClick={keep} disabled={state.busy}>
            {state.busy ? 'Working…' : 'Keep my account'}
          </button>
        ) : null}
      </div>
    </div>
  )
}
